import React from "react";
import { cn } from "../../lib/utils";
import type { MmProfile, MmProfileSource, MmRegister } from "../../api/mentalModels";
import { useMmText } from "../../hooks/useMmText";

/**
 * The four user-rated attributes (design §4.1). Everything else is AI-inferred
 * and only shown in expert register. AI values render as editable guesses.
 */
const USER_ATTRS: Array<{
  key: "resolution" | "certainty" | "charge" | "identification";
  min: number;
  max: number;
}> = [
  { key: "charge", min: -5, max: 5 },
  { key: "resolution", min: 1, max: 5 },
  { key: "certainty", min: 1, max: 5 },
  { key: "identification", min: 1, max: 5 },
];

// Universal, type-agnostic phrasings (ICDF): a goal, a memory, a value and a
// design all fit these questions. Certainty additionally carries a per-type
// hint (what "standing behind it" means for THIS kind of object).
const ATTR_FALLBACKS: Record<string, { label: string; low: string; high: string }> = {
  charge: { label: "When this comes up, how does it land in you?", low: "Weighs me down", high: "Lifts me up" },
  resolution: { label: "How clear and specific is it?", low: "Still a fog", high: "Sharp and specific" },
  certainty: { label: "How much do you stand behind it?", low: "Still forming", high: "Fully behind it" },
  identification: { label: "How close is it to who you are?", low: "Something I have", high: "Something I am" },
};

/** Soft track backgrounds — charge gets a valence gradient, scales a calm ramp. */
const TRACK_CLASS: Record<string, string> = {
  charge: "bg-gradient-to-r from-rose-300 via-slate-200 to-emerald-300 rtl:bg-gradient-to-l",
  resolution: "bg-gradient-to-r from-slate-200 to-primary/40 rtl:bg-gradient-to-l",
  certainty: "bg-gradient-to-r from-slate-200 to-primary/40 rtl:bg-gradient-to-l",
  identification: "bg-gradient-to-r from-slate-200 to-primary/40 rtl:bg-gradient-to-l",
};

const SLIDER_CLASS = cn(
  "w-full appearance-none bg-transparent cursor-pointer h-6",
  "focus-visible:outline-none",
  // WebKit thumb
  "[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5",
  "[&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white",
  "[&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-primary",
  "[&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:duration-150",
  "[&::-webkit-slider-thumb]:hover:scale-110 [&::-webkit-slider-thumb]:-mt-[7px]",
  "[&::-webkit-slider-runnable-track]:h-1.5 [&::-webkit-slider-runnable-track]:rounded-full",
  // Firefox
  "[&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:rounded-full",
  "[&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-primary [&::-moz-range-thumb]:shadow-md",
  "[&::-moz-range-track]:h-1.5 [&::-moz-range-track]:rounded-full"
);

interface Props {
  profile: MmProfile;
  profileSource?: MmProfileSource;
  confidence?: Record<string, number>;
  register: MmRegister;
  /** The object's type — lets scales speak its language (goal ≠ belief) */
  objectType?: string;
  readonly?: boolean;
  onChange: (attr: string, value: number) => void;
}

const MmProfilePanel: React.FC<Props> = ({
  profile,
  profileSource,
  confidence,
  register,
  objectType,
  readonly,
  onChange,
}) => {
  const { tm, tmt } = useMmText(register);

  return (
    <div className="space-y-5">
      {USER_ATTRS.map(({ key, min, max }) => {
        const value = profile[key];
        const source = profileSource?.[key];
        const lowConfidence = confidence !== undefined && (confidence[key] ?? 1) < 0.5;
        const fb = ATTR_FALLBACKS[key];
        const shown = value !== undefined ? value : Math.round((min + max) / 2);
        // Per-type hint (only certainty defines these today) — empty = hidden
        const hint = tmt(`attr_${key}_hint`, objectType, "");
        return (
          <div key={key}>
            <div className="flex items-center justify-between mb-1.5 gap-2">
              <label className="text-sm font-medium text-foreground leading-snug">
                {tmt(`attr_${key}_label`, objectType, fb.label)}
              </label>
              <span className="flex items-center gap-1.5 flex-shrink-0">
                {source === "ai" && !lowConfidence && (
                  <span className="text-[10px] uppercase tracking-wide rounded-full bg-violet-100 text-violet-700 px-2 py-0.5">
                    {tm("ai_guess", "AI guess")}
                  </span>
                )}
                <span
                  className={cn(
                    "min-w-[2rem] text-center text-xs font-semibold tabular-nums rounded-full px-2 py-0.5 transition-colors duration-150",
                    lowConfidence
                      ? "bg-muted text-muted-foreground"
                      : key === "charge" && typeof value === "number"
                      ? value < 0
                        ? "bg-rose-50 text-rose-700"
                        : value > 0
                        ? "bg-emerald-50 text-emerald-700"
                        : "bg-muted text-muted-foreground"
                      : "bg-primary/10 text-primary"
                  )}
                >
                  {lowConfidence ? "?" : key === "charge" && shown > 0 ? `+${shown}` : shown}
                </span>
              </span>
            </div>
            {hint && (
              <p className="text-[11px] text-muted-foreground/80 -mt-0.5 mb-1.5 leading-snug">
                {hint}
              </p>
            )}
            <div className="relative">
              <div
                className={cn(
                  "absolute inset-x-0 top-1/2 -translate-y-1/2 h-1.5 rounded-full pointer-events-none",
                  TRACK_CLASS[key]
                )}
              />
              {key === "charge" && (
                <div className="absolute start-1/2 top-1/2 -translate-y-1/2 h-3 w-px bg-slate-400/60 pointer-events-none" />
              )}
              <input
                type="range"
                min={min}
                max={max}
                step={1}
                value={shown}
                disabled={readonly}
                aria-label={tmt(`attr_${key}_label`, objectType, fb.label)}
                onChange={(e) => onChange(key, Number(e.target.value))}
                className={cn(SLIDER_CLASS, "relative", readonly && "opacity-60 pointer-events-none")}
              />
            </div>
            <div className="flex justify-between text-[11px] text-muted-foreground/80 mt-0.5">
              <span>{tmt(`attr_${key}_low`, objectType, fb.low)}</span>
              <span>{tmt(`attr_${key}_high`, objectType, fb.high)}</span>
            </div>
          </div>
        );
      })}

      {register === "expert" && (
        <div className="pt-3 border-t border-border/60 text-xs text-muted-foreground space-y-1.5">
          {(["frame", "complexity", "boundaries", "orientation", "malleability"] as const).map(
            (attr) =>
              profile[attr] !== undefined && (
                <div key={attr} className="flex justify-between">
                  <span className="capitalize">{attr}</span>
                  <span className="tabular-nums">
                    {String(profile[attr])}
                    {profileSource?.[attr] ? ` (${profileSource[attr]})` : ""}
                  </span>
                </div>
              )
          )}
        </div>
      )}
    </div>
  );
};

export default MmProfilePanel;
